import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Package, Building2, Network, AlertTriangle, Loader2, FileText } from 'lucide-react';
import { Input } from '../ui/input';
import { coreAPI, purchasingAPI, suppliersAPI } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { useAuth } from '../../contexts/AuthContext';

interface SearchResult {
  id: number;
  title: string;
  subtitle: string;
  category: 'order' | 'supplier' | 'supplier_group' | 'quality';
  url: string;
}

/**
 * CR-3954: Global search bar component for TopBar.
 * Searches across POs and suppliers, showing results in a dropdown.
 */
export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  // CR-4763: Compliance-only tenants (e.g. MAB) don't have the purchasing
  // module, so the "orders, suppliers, products" hint is misleading. Fall
  // back to a plain "Search" label when either the portal is explicitly in
  // compliance mode OR the purchasing menu item is disabled for the tenant
  // (MAB runs portal_mode='standard' with admin-hidden modules).
  const { isComplianceMode, isMenuItemVisible } = useMenuSettings();
  const { user } = useAuth();
  // The group list is Helix-only (CR-5966 added it for internal admins); for
  // every other role the call can only 403, so don't make it.
  const isHelixUser = user?.company_type === 'helix';
  const hasPurchasing = isMenuItemVisible('purchasing');
  const simpleSearch = isComplianceMode || !hasPurchasing;
  const searchHint = simpleSearch ? 'Search' : 'Search orders, suppliers, products';

  const performSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      const searchResults: SearchResult[] = [];

      // Search POs, supplier sites, and supplier groups in parallel.
      // CR-5966: include CompanyGroup results so internal/Valuechain admins
      // can jump straight to a supplier *group* (e.g. "Skybridge
      // Components") from the global search — previously only the
      // individual supplier sites (Americas / Europe / UK) surfaced,
      // and there was no way to reach the group from the search bar.
      const [poResponse, supplierResponse, groupResponse] = await Promise.allSettled([
        purchasingAPI.getOrders({ search: searchQuery, page_size: 5 }),
        suppliersAPI.getProfiles({ search: searchQuery, page_size: 5 }),
        isHelixUser
          ? coreAPI.getCompanyGroups({
            search: searchQuery,
            company_type: 'supplier',
            page_size: 5,
          })
          : Promise.resolve({ data: { results: [] } }),
      ]);

      if (poResponse.status === 'fulfilled') {
        poResponse.value.data.results.forEach((po) => {
          searchResults.push({
            id: po.id,
            title: po.po_number,
            subtitle: `${po.supplier_name || 'Unknown'} - ${po.status}`,
            category: 'order',
            url: `/purchasing?po=${po.id}`,
          });
        });
      }

      if (groupResponse.status === 'fulfilled') {
        // Surface supplier groups *above* individual sites so the
        // group-level entry is the first thing a user sees when their
        // query matches both — matches the "drill from group → site"
        // mental model used throughout the app.
        groupResponse.value.data.results.forEach((group) => {
          const memberLabel = group.company_count === 1 ? 'site' : 'sites';
          searchResults.push({
            id: group.id,
            title: group.name,
            subtitle: `${group.company_count} ${memberLabel}`,
            category: 'supplier_group',
            url: `/suppliers/groups/${group.id}`,
          });
        });
      }

      if (supplierResponse.status === 'fulfilled') {
        supplierResponse.value.data.results.forEach((supplier) => {
          // CR-5967: SupplierDetail's route param is the Company id (the
          // SupplierProfileViewSet uses lookup_field='company_id'). The
          // default list endpoint uses OptimizedSupplierProfileSerializer,
          // which exposes the FK as `company` and does NOT alias it to
          // `company_id` (only MinimalSupplierListSerializer does). Reading
          // `supplier.company_id` here yielded `undefined`, navigating the
          // user to `/suppliers/undefined` → "Error: Not found." Use the
          // FK field that every serializer in the chain emits.
          searchResults.push({
            id: supplier.id,
            title: supplier.company_name,
            subtitle: `Rating: ${supplier.overall_rating || 'N/A'}`,
            category: 'supplier',
            url: `/suppliers/${supplier.company}`,
          });
        });
      }

      setResults(searchResults);
      setShowDropdown(searchResults.length > 0);
      setSelectedIndex(-1);
    } catch {
      // Silent fail - search is non-critical
    } finally {
      setLoading(false);
    }
  }, [isHelixUser]);

  const handleChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => performSearch(value), 300);
  };

  const handleSelect = (result: SearchResult) => {
    setShowDropdown(false);
    setQuery('');
    navigate(result.url);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && results[selectedIndex]) {
        handleSelect(results[selectedIndex]);
      } else if (query.length >= 2) {
        // Default: navigate to purchasing with search
        setShowDropdown(false);
        navigate(`/purchasing?search=${encodeURIComponent(query)}`);
        setQuery('');
      }
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'order': return <Package className="h-4 w-4 text-blue-500" />;
      case 'supplier': return <Building2 className="h-4 w-4 text-green-500" />;
      case 'supplier_group': return <Network className="h-4 w-4 text-emerald-600" />;
      case 'quality': return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      default: return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getCategoryLabel = (category: string) => {
    // 'supplier_group' would render as "supplier group" with capitalize CSS,
    // but that's still a bit clunky. Show "Group" instead so the right-hand
    // chip differentiates the group entry from sibling supplier-site rows.
    if (category === 'supplier_group') return 'Group';
    return category;
  };

  return (
    <div className="w-full max-w-lg relative">
      <label htmlFor="global-search" className="sr-only">
        {searchHint}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {loading ? (
            <Loader2 className="h-5 w-5 text-gray-400 animate-spin" aria-hidden="true" />
          ) : (
            <Search className="h-5 w-5 text-gray-400 dark:text-muted-foreground" aria-hidden="true" />
          )}
        </div>
        <Input
          ref={inputRef}
          id="global-search"
          name="search"
          className="pl-10 bg-white dark:bg-background"
          placeholder={simpleSearch ? 'Search' : 'Search orders, suppliers, products...'}
          type="search"
          autoComplete="off"
          aria-label={searchHint}
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
        />
      </div>

      {/* Search Results Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-card border border-gray-200 dark:border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto"
        >
          {results.map((result, index) => (
            <button
              key={`${result.category}-${result.id}`}
              type="button"
              className={cn(
                'w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-accent transition-colors',
                index === selectedIndex && 'bg-gray-50 dark:bg-accent'
              )}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              {getCategoryIcon(result.category)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-foreground truncate">
                  {result.title}
                </p>
                <p className="text-xs text-gray-500 dark:text-muted-foreground truncate">
                  {result.subtitle}
                </p>
              </div>
              <span className="text-xs text-gray-400 capitalize">{getCategoryLabel(result.category)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
